import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getMatchDetails } from "../services/ScheduleService";

function MatchDetail() {
    const { matchId } = useParams();
    const navigate = useNavigate();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeInning, setActiveInning] = useState(0);

    useEffect(() => {
        getMatchDetails(matchId).then((data) => {
            setDetails(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [matchId]);

    const getWinnerName = () => {
        if (!details?.winningTeamId) return null;
        if (details.winningTeamId === details.team1Id) return details.team1Name;
        if (details.winningTeamId === details.team2Id) return details.team2Name;
        return null;
    };

    const getBallStyle = (outcome) => {
        if (!outcome) return { background: "var(--bg3)", color: "var(--text2)" };
        const o = outcome.toUpperCase();
        if (o.includes("SIX")) return { background: "var(--lime)", color: "#000" };
        if (o.includes("FOUR")) return { background: "var(--neon)", color: "#000" };
        if (o.includes("OUT") || o.includes("WICKET")) return { background: "#e74c3c", color: "#fff" };
        if (o.includes("WIDE") || o.includes("NO_BALL")) return { background: "var(--text3)", color: "#fff" };
        return { background: "var(--bg3)", color: "var(--text2)" };
    };

    const getBallLabel = (outcome) => {
        if (!outcome) return "·";
        const o = outcome.toUpperCase();
        if (o.includes("SIX")) return "6";
        if (o.includes("FOUR")) return "4";
        if (o.includes("OUT") || o.includes("WICKET")) return "W";
        if (o.includes("WIDE")) return "Wd";
        if (o.includes("NO_BALL")) return "Nb";
        if (o.includes("ONE")) return "1";
        if (o.includes("TWO")) return "2";
        if (o.includes("THREE")) return "3";
        return "·";
    };

    if (loading) return (
        <div className="main-wrap main-wrap-center">
            Loading match details...
        </div>
    );

    if (!details) return (
        <div className="main-wrap main-wrap-center">
            Match not found.
        </div>
    );

    const inning = details.innings?.[activeInning];

    return (
        <div className="main-wrap main-wrap-padded">

            {/* Back Button */}
            <button className="fbtn back-btn" onClick={() => navigate("/results")}>
                ← Back to Results
            </button>
            {/* Match Header */}
            <div className="sfull md-header">
                <div className="md-header-inner">
                    <div className="md-team-col">
                        <img src={details.team1PicURL} alt={details.team1Name}
                             className="md-team-img"
                             onError={(e) => e.target.style.display = "none"} />
                        <div className="md-team-name">{details.team1Name}</div>
                    </div>
                    <div className="md-vs-col">
                        <div className="md-vs-label">VS</div>
                        {getWinnerName() && (
                            <div className="md-winner">{getWinnerName()} won</div>
                        )}
                        <div className="md-match-id">#{details.matchId}</div>
                    </div>
                    <div className="md-team-col">
                        <img src={details.team2PicURL} alt={details.team2Name}
                             className="md-team-img"
                             onError={(e) => e.target.style.display = "none"} />
                        <div className="md-team-name">{details.team2Name}</div>
                    </div>
                </div>
            </div>

            {/* Innings Tabs */}
            {details.innings?.length > 0 && (
                <div className="fbar md-innings-bar">
                    {details.innings.map((inn, i) => (
                        <button key={i}
                                className={`fbtn ${activeInning === i ? "active" : ""}`}
                                onClick={() => setActiveInning(i)}>
                            {inn.battingTeamName || `Inning ${i + 1}`}
                        </button>
                    ))}
                </div>
            )}

            {inning && (
                <>
                    {/* Inning Score Summary */}
                    <div className="sfull md-score-box">
                        <div className="md-score-inner">
                            <div>
                                <span className="md-score-runs">{inning.runs}/{inning.wickets}</span>
                                <span className="md-score-overs">({inning.numberOfOvers} ov)</span>
                            </div>
                            <div className="md-score-stats">
                                <div className="md-stat-col">
                                    <div className="md-stat-val">{inning.fours}</div>
                                    <div className="md-stat-label">4s</div>
                                </div>
                                <div className="md-stat-col">
                                    <div className="md-stat-val">{inning.sixes}</div>
                                    <div className="md-stat-label">6s</div>
                                </div>
                                <div className="md-stat-col">
                                    <div className="md-stat-val">{inning.dots}</div>
                                    <div className="md-stat-label">DOTS</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Ball by Ball */}
                    {inning.ballByBall?.length > 0 && (
                        <div className="sfull md-bbb-box">
                            <div className="md-bbb-title">BALL BY BALL</div>
                            <div className="md-bbb-grid">
                                {inning.ballByBall.map((ball, i) => (
                                    <div key={i}
                                         title={`Over ${ball.numberOfOver}.${ball.ballOfOver} — ${ball.batsmanName} vs ${ball.bowlerName}`}
                                         style={{
                                             width: "32px", height: "32px", borderRadius: "50%",
                                             display: "flex", alignItems: "center", justifyContent: "center",
                                             fontSize: "11px", fontWeight: 700, cursor: "default",
                                             ...getBallStyle(ball.actionOutCome)
                                         }}>
                                        {getBallLabel(ball.actionOutCome)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Batting Scorecard */}
                    {inning.battingDetails?.length > 0 && (
                        <div className="sfull md-scorecard-box">
                            <div className="md-scorecard-header">
                                <span className="md-scorecard-title-bat">BATTING</span>
                            </div>
                            <table className="md-table">
                                <thead>
                                <tr>
                                    {["BATTER", "R", "B", "4s", "6s", ""].map((h, i) => (
                                        <th key={i} className={`md-th ${i === 0 ? "md-th-left" : "md-th-center"}`}>{h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {inning.battingDetails.map((b, i) => (
                                    <tr key={i} className="md-tr">
                                        <td className="md-td-player">
                                            <div className="md-td-player-name">{b.playerName}</div>
                                            {b.dismissed && (
                                                <div className="md-td-dismissal">
                                                    {b.dismissalType?.toLowerCase().replace(/_/g, " ")}
                                                    {b.bowledByName ? ` b ${b.bowledByName}` : ""}
                                                    {b.caughtByName ? ` c ${b.caughtByName}` : ""}
                                                </div>
                                            )}
                                            {!b.dismissed && <div className="md-td-notout">not out</div>}
                                        </td>
                                        <td className="md-td-runs">{b.runs}</td>
                                        <td className="md-td-center">{b.bowls}</td>
                                        <td className="md-td-center">{b.fours}</td>
                                        <td className="md-td-center">{b.sixes}</td>
                                        <td className="md-td-status">
                                            {b.dismissed
                                                ? <span className="md-out">OUT</span>
                                                : <span className="md-notout-star">*</span>}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Bowling Scorecard */}
                    {inning.bowlingDetails?.length > 0 && (
                        <div className="sfull md-scorecard-box">
                            <div className="md-scorecard-header">
                                <span className="md-scorecard-title-bowl">BOWLING</span>
                            </div>
                            <table className="md-table">
                                <thead>
                                <tr>
                                    {["BOWLER", "O", "R", "W", "DOTS"].map((h, i) => (
                                        <th key={i} className={`md-th ${i === 0 ? "md-th-left" : "md-th-center"}`}>{h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {inning.bowlingDetails.map((b, i) => (
                                    <tr key={i} className="md-tr">
                                        <td className="md-td-bowler">{b.playerName}</td>
                                        <td className="md-td-center">{Math.floor(b.balls / 6)}.{b.balls % 6}</td>
                                        <td className="md-td-center">{b.runs}</td>
                                        <td className="md-td-wickets">{b.wickets}</td>
                                        <td className="md-td-center">{b.dots}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default MatchDetail;